import React, { useState, useEffect, useCallback, useLayoutEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  TextInput,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { PrivateWaypoint, WaypointCategory } from '../types';
import { PrivateWaypointsService } from '../services/privateWaypoints';
import { GPXParser } from '../services/gpxParser';
import WaypointEditModal from '../components/WaypointEditModal';
import WaypointCategoryFilter from '../components/WaypointCategoryFilter';

// Define colors
const colors = {
  primary: '#007AFF',
  secondary: '#5856D6',
  success: '#34C759',
  warning: '#FF9500',
  error: '#FF3B30',
  info: '#5AC8FA',
  background: '#F2F2F7',
  surface: '#FFFFFF',
  text: '#000000',
  textSecondary: '#8E8E93',
  border: '#E5E5EA',
};

// Define fonts
const fonts = {
  regular: 'System',
  semiBold: 'System',
  bold: 'System',
};

const WaypointsScreen: React.FC = () => {
  const navigation = useNavigation();
  const [allWaypoints, setAllWaypoints] = useState<PrivateWaypoint[]>([]);
  const [displayedWaypoints, setDisplayedWaypoints] = useState<PrivateWaypoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<WaypointCategory | 'all'>('all');
  const [editingWaypoint, setEditingWaypoint] = useState<PrivateWaypoint | null>(null);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Configure navigation header with action buttons
  useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <View style={{ flexDirection: 'row' }}>
          <TouchableOpacity
            style={{ marginRight: 16 }}
            onPress={() => handleExportGPX()}
          >
            <Icon name="share" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          <TouchableOpacity
            style={{ marginRight: 16 }}
            onPress={() => handleImportGPX()}
          >
            <Icon name="download" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          <TouchableOpacity
            style={{ marginRight: 16 }}
            onPress={() => setIsCreateModalVisible(true)}
          >
            <Icon name="add" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      ),
    });
  }, [navigation]);

  // Load all waypoints once and cache them
  const loadAllWaypoints = useCallback(async (forceRefresh = false) => {
    if (!isInitialLoad && !forceRefresh) return;
    
    try {
      setLoading(true);
      
      // Clear cache if forcing refresh or if we detect old 1000 limit
      if (forceRefresh) {
        await PrivateWaypointsService.clearCache();
      }
      
      // Load all waypoints in batches for better performance
      const waypoints = await PrivateWaypointsService.getAllWaypointsOptimized();
      
      // Check if we got the old 1000 limit and force refresh if so
      if (waypoints.length === 1000) {
        console.log('Detected old 1000 limit cache, clearing and reloading...');
        await PrivateWaypointsService.clearCache();
        const freshWaypoints = await PrivateWaypointsService.getAllWaypointsOptimized();
        waypoints.length = 0;
        waypoints.push(...freshWaypoints);
      }
      
      // Ensure uniqueness and sort by name
      const uniqueWaypoints = Array.from(
        new Map(waypoints.map(wp => [wp.id, wp])).values()
      ).sort((a, b) => a.name.localeCompare(b.name));
      
      setAllWaypoints(uniqueWaypoints);
      setDisplayedWaypoints(uniqueWaypoints);
      setIsInitialLoad(false);
    } catch (error) {
      console.error('Error loading waypoints:', error);
      Alert.alert('Error', 'Failed to load waypoints');
    } finally {
      setLoading(false);
    }
  }, [isInitialLoad]);

  // Fast local filtering
  const filterWaypoints = useCallback(() => {
    let filtered = [...allWaypoints];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(wp => 
        wp.name.toLowerCase().includes(searchLower) ||
        wp.description?.toLowerCase().includes(searchLower) ||
        wp.tags?.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(wp => wp.category === selectedCategory);
    }
    
    setDisplayedWaypoints(filtered);
  }, [allWaypoints, searchQuery, selectedCategory]);

  // Load waypoints once when screen is focused
  useFocusEffect(
    useCallback(() => {
      // Force refresh on first load if we have the old 1000 limit
      loadAllWaypoints(allWaypoints.length === 1000);
    }, [loadAllWaypoints, allWaypoints.length])
  );

  // Instant local filtering when search or category changes
  useEffect(() => {
    filterWaypoints();
  }, [searchQuery, selectedCategory, filterWaypoints]);

  // Reload waypoints from server (force refresh)
  const reloadWaypoints = useCallback(async () => {
    await loadAllWaypoints(true);
  }, [loadAllWaypoints]);

  const handleImportGPX = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*',
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets && result.assets[0]) {
        const fileUri = result.assets[0].uri;
        const gpxContent = await FileSystem.readAsStringAsync(fileUri);
        
        const parsedWaypoints = await GPXParser.parseGPXFile(gpxContent);
        
        // For import, we still need all waypoints to check duplicates
        // This is a special case where loading all is necessary
        const existingWaypoints = await PrivateWaypointsService.getWaypoints();
        const duplicates: typeof parsedWaypoints = [];
        const newWaypoints: typeof parsedWaypoints = [];
        
        parsedWaypoints.forEach(parsed => {
          const isDuplicate = existingWaypoints.some(existing => 
            Math.abs(existing.coordinates.latitude - parsed.coordinates.latitude) < 0.000001 &&
            Math.abs(existing.coordinates.longitude - parsed.coordinates.longitude) < 0.000001
          );
          
          if (isDuplicate) {
            duplicates.push(parsed);
          } else {
            newWaypoints.push(parsed);
          }
        });
        
        let message = `Found ${parsedWaypoints.length} waypoints.`;
        if (duplicates.length > 0) {
          message += `\n\n${duplicates.length} appear to be duplicates.`;
          message += `\n${newWaypoints.length} are new.`;
        }
        message += '\n\nImport new waypoints?';
        
        Alert.alert(
          'Import Waypoints',
          message,
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Import All',
              onPress: async () => {
                try {
                  await PrivateWaypointsService.importWaypoints(parsedWaypoints);
                  Alert.alert('Success', `Imported ${parsedWaypoints.length} waypoints`);
                  await reloadWaypoints();
                } catch (error) {
                  Alert.alert('Error', 'Failed to import waypoints');
                }
              },
            },
            newWaypoints.length > 0 && {
              text: 'Import New Only',
              onPress: async () => {
                try {
                  await PrivateWaypointsService.importWaypoints(newWaypoints);
                  Alert.alert('Success', `Imported ${newWaypoints.length} new waypoints`);
                  await reloadWaypoints();
                } catch (error) {
                  Alert.alert('Error', 'Failed to import waypoints');
                }
              },
            },
          ].filter(Boolean) as Array<{ text: string; style?: 'cancel' | 'destructive'; onPress?: () => void | Promise<void> }>
        );
      }
    } catch (error) {
      console.error('Error importing GPX:', error);
      Alert.alert('Error', 'Failed to import GPX file');
    }
  };

  const handleExportGPX = async () => {
    try {
      // For export, load all waypoints
      const allWaypoints = await PrivateWaypointsService.getWaypoints();
      const gpxContent = GPXParser.generateGPX(allWaypoints);
      const fileName = `waypoints_${new Date().toISOString().split('T')[0]}.gpx`;
      const path = `${FileSystem.documentDirectory}${fileName}`;
      
      await FileSystem.writeAsStringAsync(path, gpxContent, {
        encoding: FileSystem.EncodingType.UTF8,
      });
      
      // Share the file
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(path);
      } else {
        Alert.alert('Success', `Waypoints exported to ${fileName}`);
      }
    } catch (error) {
      console.error('Error exporting GPX:', error);
      Alert.alert('Error', 'Failed to export waypoints');
    }
  };

  const handleDeleteWaypoint = (waypoint: PrivateWaypoint) => {
    Alert.alert(
      'Delete Waypoint',
      `Are you sure you want to delete "${waypoint.name}"?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await PrivateWaypointsService.deleteWaypoint(waypoint.id);
              // Remove from local state immediately for better UX
              setAllWaypoints(prev => prev.filter(wp => wp.id !== waypoint.id));
              setDisplayedWaypoints(prev => prev.filter(wp => wp.id !== waypoint.id));
            } catch (error) {
              Alert.alert('Error', 'Failed to delete waypoint');
              // Reload on error to sync state
              await reloadWaypoints();
            }
          },
        },
      ]
    );
  };

  const getCategoryIcon = (category: WaypointCategory): string => {
    switch (category) {
      case 'fishing_spot': return 'phishing';
      case 'reef': return 'terrain';
      case 'wreck': return 'directions-boat';
      case 'structure': return 'account-balance';
      case 'anchorage': return 'anchor';
      case 'hazard': return 'warning';
      case 'personal': return 'person-pin';
      default: return 'place';
    }
  };

  const getCategoryColor = (category: WaypointCategory): string => {
    switch (category) {
      case 'fishing_spot': return colors.primary;
      case 'reef': return colors.success;
      case 'wreck': return colors.warning;
      case 'structure': return colors.info;
      case 'anchorage': return colors.secondary;
      case 'hazard': return colors.error;
      case 'personal': return colors.text;
      default: return colors.textSecondary;
    }
  };

  const renderWaypoint = useCallback(({ item }: { item: PrivateWaypoint }) => (
    <TouchableOpacity
      style={styles.waypointCard}
      onPress={() => setEditingWaypoint(item)}
      activeOpacity={0.7}
    >
      <View style={styles.waypointContent}>
        <Icon
          name={getCategoryIcon(item.category)}
          size={32}
          color={getCategoryColor(item.category)}
          style={styles.waypointIcon}
        />
        <View style={styles.waypointInfo}>
          <Text style={styles.waypointName}>{item.name}</Text>
          {item.description && (
            <Text style={styles.waypointDescription} numberOfLines={1}>
              {item.description}
            </Text>
          )}
          <Text style={styles.waypointCoords}>
            {item.coordinates.latitude.toFixed(6)}, {item.coordinates.longitude.toFixed(6)}
          </Text>
        </View>
        <TouchableOpacity
          onPress={() => handleDeleteWaypoint(item)}
          style={styles.deleteButton}
        >
          <Icon name="delete" size={24} color={colors.error} />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  ), []);

  return (
    <View style={styles.container}>

      <View style={styles.searchContainer}>
        <Icon name="search" size={20} color={colors.textSecondary} style={styles.searchIcon} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search waypoints..."
          placeholderTextColor={colors.textSecondary}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
      </View>

      <View style={styles.categoryFilterContainer}>
        <WaypointCategoryFilter
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
        />
      </View>

      {loading ? (
        <View style={styles.skeletonContainer}>
          {[...Array(8)].map((_, index) => (
            <View key={index} style={styles.skeletonCard}>
              <View style={styles.skeletonIcon} />
              <View style={styles.skeletonContent}>
                <View style={[styles.skeletonText, { width: '60%' }]} />
                <View style={[styles.skeletonText, { width: '40%', opacity: 0.5 }]} />
                <View style={[styles.skeletonText, { width: '80%', opacity: 0.3 }]} />
              </View>
              <View style={styles.skeletonAction} />
            </View>
          ))}
        </View>
      ) : (
        <FlatList
          data={displayedWaypoints}
          renderItem={renderWaypoint}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.listContent}
          ListFooterComponent={
            displayedWaypoints.length > 0 ? (
              <View style={styles.endOfList}>
                <Text style={styles.endOfListText}>
                  {displayedWaypoints.length} of {allWaypoints.length} waypoint{allWaypoints.length !== 1 ? 's' : ''}
                  {searchQuery || selectedCategory !== 'all' ? ' shown' : ''}
                </Text>
              </View>
            ) : null
          }
          ListEmptyComponent={
            !loading ? (
              <View style={styles.emptyContainer}>
                <Icon name="place" size={64} color={colors.textSecondary} />
                <Text style={styles.emptyText}>
                  {searchQuery || selectedCategory !== 'all' ? 'No matching waypoints' : 'No waypoints found'}
                </Text>
                <Text style={styles.emptySubtext}>
                  {searchQuery || selectedCategory !== 'all' 
                    ? 'Try adjusting your search or filters'
                    : 'Create a new waypoint or import from GPX'}
                </Text>
              </View>
            ) : null
          }
          initialNumToRender={10}
          maxToRenderPerBatch={10}
          windowSize={10}
          removeClippedSubviews={true}
          updateCellsBatchingPeriod={10}
          getItemLayout={(data, index) => ({
            length: 88, // Approximate height of each waypoint card
            offset: 88 * index,
            index,
          })}
        />
      )}

      {(editingWaypoint || isCreateModalVisible) && (
        <WaypointEditModal
          waypoint={editingWaypoint}
          visible={!!editingWaypoint || isCreateModalVisible}
          onClose={() => {
            setEditingWaypoint(null);
            setIsCreateModalVisible(false);
          }}
          onSave={async (waypoint) => {
            await reloadWaypoints();
            setEditingWaypoint(null);
            setIsCreateModalVisible(false);
          }}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: colors.surface,
    minHeight: 60,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 4,
      },
    }),
  },
  title: {
    fontSize: 24,
    fontFamily: fonts.bold,
    color: colors.text,
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerButton: {
    padding: 8,
    marginLeft: 12,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    margin: 16,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 12,
    fontSize: 16,
    fontFamily: fonts.regular,
    color: colors.text,
  },
  categoryFilterContainer: {
    backgroundColor: colors.background,
    paddingBottom: 8,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    fontFamily: fonts.regular,
    color: colors.textSecondary,
  },
  listContent: {
    paddingBottom: 100,
  },
  waypointCard: {
    backgroundColor: colors.surface,
    marginHorizontal: 16,
    marginVertical: 6,
    borderRadius: 12,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  waypointContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  waypointIcon: {
    marginRight: 16,
  },
  waypointInfo: {
    flex: 1,
  },
  waypointName: {
    fontSize: 16,
    fontFamily: fonts.semiBold,
    color: colors.text,
    marginBottom: 4,
  },
  waypointDescription: {
    fontSize: 14,
    fontFamily: fonts.regular,
    color: colors.textSecondary,
    marginBottom: 4,
  },
  waypointCoords: {
    fontSize: 12,
    fontFamily: fonts.regular,
    color: colors.textSecondary,
  },
  deleteButton: {
    padding: 8,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 100,
  },
  emptyText: {
    fontSize: 18,
    fontFamily: fonts.semiBold,
    color: colors.text,
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    fontFamily: fonts.regular,
    color: colors.textSecondary,
    marginTop: 8,
  },
  loadingMore: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 20,
  },
  loadingMoreText: {
    marginLeft: 10,
    fontSize: 14,
    fontFamily: fonts.regular,
    color: colors.textSecondary,
  },
  loadMoreButton: {
    paddingVertical: 16,
    paddingHorizontal: 32,
    backgroundColor: colors.primary,
    borderRadius: 8,
    marginVertical: 20,
    alignSelf: 'center',
  },
  loadMoreButtonText: {
    color: colors.surface,
    fontSize: 14,
    fontFamily: fonts.semiBold,
  },
  endOfList: {
    paddingVertical: 20,
    alignItems: 'center',
  },
  endOfListText: {
    fontSize: 14,
    fontFamily: fonts.regular,
    color: colors.textSecondary,
  },
  skeletonContainer: {
    flex: 1,
    paddingTop: 8,
  },
  skeletonCard: {
    backgroundColor: colors.surface,
    marginHorizontal: 16,
    marginVertical: 6,
    borderRadius: 12,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 4,
      },
      android: {
        elevation: 1,
      },
    }),
  },
  skeletonIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.border,
    marginRight: 16,
  },
  skeletonContent: {
    flex: 1,
  },
  skeletonText: {
    height: 12,
    backgroundColor: colors.border,
    borderRadius: 6,
    marginBottom: 6,
  },
  skeletonAction: {
    width: 24,
    height: 24,
    borderRadius: 4,
    backgroundColor: colors.border,
    marginLeft: 8,
  },
});

export default WaypointsScreen;